import React, { CSSProperties, useState } from 'react'
import { Box, Button, Icon, styled, Typography } from '@mui/material'
import { useDropzone } from 'react-dropzone'
import { CloudUploadSvg } from '../../assets'
import { CssStyles } from '../../types'
import { getClassName, parseClassNames } from '../../utils'
import { cx } from '@emotion/css'

const IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'svg', 'gif', 'webp']
const LABEL = 'Drop files to instantly upload them'

type ObjectType = { [key: string]: unknown }

interface FileMetadata {
  fileName?: string
  extension?: string
  width?: number
  height?: number
}

interface IFileUpload {
  files: File[]
  onBrowseLabel?: string
  label?: string
  position?: 'top' | 'bottom' | 'center'
  /** *Deprecated* Raw CSS styles
   * @deprecated
   */
  styles?: CssStyles<
    | 'root'
    | 'icon'
    | 'label'
    | 'browseButton'
    | 'browseLabel'
    | 'files'
    | 'file'
    | 'fileIcon'
    | 'fileName'
    | 'fileDeleteButton'
    | 'fileDeleteIcon'
  >
  messageStyle: 'ribbon' | 'box'
  onChange: (newFile: File, metadata: FileMetadata) => void
  onRemove: (oldFile: File, index: number) => void

  style?: CSSProperties | string
  iconStyle?: CSSProperties | string
  labelStyle?: CSSProperties | string
  browseButtonStyle?: CSSProperties | string
  browseLabelStyle?: CSSProperties | string
  filesStyle?: CSSProperties | string
  fileStyle?: CSSProperties | string
  fileIconStyle?: CSSProperties | string
  fileNameStyle?: CSSProperties | string
  fileDeleteButtonStyle?: CSSProperties | string
  fileDeleteIconStyle?: CSSProperties | string
}

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: 16px;
  padding-bottom: 16px;
  width: 100%;
  height: calc(170px - 32px);
  border-radius: 4px;
  border: 2px dashed lightgray;
  :hover {
    background-color: #e3f2fd;
  }
`

const FileItem = styled(Box)`
  width: 100%;
  height: 48px;
  border: 1px solid lightgray;
  display: flex;
  align-items: center;
`

const FilesWrapper = styled(Box)`
  display: flex;
  margin-top: 20px;
  align-items: center;
`

const LabelsWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-left: 12px;
  align-items: flex-start;
  justify-content: center;
`

const ContentWrapper = styled(Box)`
  display: flex;
  width: 100%;
  height: 56px;
  justify-content: center;
  align-items: center;
`

export const FileUpload = React.forwardRef<HTMLDivElement, IFileUpload>(
  (
    {
      files,
      styles,
      onChange,
      onRemove,
      onBrowseLabel,
      label = LABEL,
      position = 'center',
      messageStyle = 'ribbon',
      style,
      iconStyle,
      labelStyle,
      browseButtonStyle,
      browseLabelStyle,
      filesStyle,
      fileStyle,
      fileIconStyle,
      fileNameStyle,
      fileDeleteButtonStyle,
      fileDeleteIconStyle,
    },
    ref
  ) => {
    const [hover, setHover] = useState<boolean>(false)
    const [dragOver, setDragOver] = useState<boolean>(false)

    const classNames = styles && parseClassNames(styles)

    const handleFileDrop = (files: File[]) => {
      setDragOver(false)
      files.forEach((file: File) => handleFileInput(file))
    }

    const { getRootProps, getInputProps, open } = useDropzone({
      onDropAccepted: handleFileDrop,
      noClick: true,
      noKeyboard: true,
    })

    const handleFileInput = (droppedFile: File) => {
      const fileName = droppedFile?.name ?? ''

      if (droppedFile && fileName) {
        const fileNameParams = fileName.split('.')
        const extension =
          fileNameParams.length > 1
            ? fileNameParams[fileNameParams.length - 1].toLowerCase()
            : ''

        if (IMAGE_TYPES.includes(extension)) {
          const reader = new FileReader()
          reader.readAsDataURL(droppedFile)
          reader.onload = function (e) {
            const image = new Image()
            image.src = (e.target?.result as string) || ''
            image.onload = async function () {
              const height: number = (this as unknown as ObjectType)
                .height as number
              const width: number = (this as unknown as ObjectType)
                .width as number

              onChange(droppedFile, { fileName, extension, width, height })
            }
          }
        } else {
          onChange(droppedFile, { fileName, extension })
        }
      }
    }

    const calculatePosition = (position: 'top' | 'bottom' | 'center') => {
      switch (position) {
        case 'top':
          return 'flex-start'
        case 'center':
          return 'center'
        case 'bottom':
          return 'flex-end'
        default:
          return 'center'
      }
    }

    return (
      <>
        <Wrapper
          sx={(theme) => ({
            backgroundColor: dragOver
              ? theme.palette.primary.main
              : hover
              ? theme.palette.primary.main
              : 'transparent',
            border: dragOver
              ? `2px dashed ${theme.palette.primary.main}`
              : hover
              ? `2px dashed ${theme.palette.primary.main}`
              : '2px dashed lightgray',
            alignItems: calculatePosition(position),
          })}
          {...getRootProps()}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onDragLeave={() => setDragOver(false)}
          onDragOver={() => setDragOver(true)}
          onDragEnd={() => setDragOver(false)}
          className={cx(classNames?.root, getClassName(style))}
          ref={ref}
        >
          <input {...getInputProps()} />
          <ContentWrapper
            sx={(theme) => ({
              backgroundColor: dragOver
                ? theme.palette.primary.main
                : hover
                ? theme.palette.primary.main
                : 'transparent',
              flexDirection: messageStyle === 'ribbon' ? 'row' : 'column',
              width: messageStyle === 'ribbon' ? '100%' : '140px',
              height: messageStyle === 'ribbon' ? '56px' : '110px',
              borderRadius: messageStyle === 'ribbon' ? 0 : '4px',
            })}
          >
            <Box className={cx(classNames?.icon, getClassName(iconStyle))}>
              <CloudUploadSvg
                color={
                  dragOver
                    ? 'rgba(255, 255, 255, 0.7)'
                    : hover
                    ? 'rgba(255, 255, 255, 0.7)'
                    : '#2196F3'
                }
                width={50}
                height={50}
              />
            </Box>
            <LabelsWrapper
              sx={{
                alignItems: messageStyle === 'ribbon' ? 'flex-start' : 'center',
                marginLeft: messageStyle === 'ribbon' ? '12px' : 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: messageStyle === 'ribbon' ? '16px' : '12px',
                  lineHeight: messageStyle === 'ribbon' ? '24px' : '16px',
                  fontWeight: 400,
                  letterSpacing: messageStyle === 'ribbon' ? 0.5 : 0.4,
                  textAlign: 'center',
                }}
                color={dragOver ? '#FFFFFF' : hover ? '#FFFFFF' : 'black'}
                className={cx(classNames?.label, getClassName(labelStyle))}
              >
                {label}
              </Typography>
              {onBrowseLabel && (
                <Button
                  variant="text"
                  sx={{ textTransform: 'none', padding: 0 }}
                  onClick={open}
                  className={cx(
                    classNames?.browseButton,
                    getClassName(browseButtonStyle)
                  )}
                >
                  <Typography
                    color={
                      dragOver ? 'lightgray' : hover ? 'lightgray' : 'GrayText'
                    }
                    variant="body1"
                    sx={{
                      fontSize: messageStyle === 'ribbon' ? '12px' : '10px',
                      lineHeight: messageStyle === 'ribbon' ? '16px' : '12px',
                      letterSpacing: 0.4,
                      fontWeight: 400,
                    }}
                    className={cx(
                      classNames?.browseLabel,
                      getClassName(browseLabelStyle)
                    )}
                  >
                    {onBrowseLabel}
                  </Typography>
                </Button>
              )}
            </LabelsWrapper>
          </ContentWrapper>
        </Wrapper>
        <FilesWrapper
          className={cx(classNames?.files, getClassName(filesStyle))}
        >
          {files &&
            files.length > 0 &&
            files.map((file: File, index: number) => (
              <FileItem
                key={file.name}
                className={cx(classNames?.file, getClassName(fileStyle))}
              >
                <Icon
                  sx={{ ml: '20px' }}
                  className={cx(
                    classNames?.fileIcon,
                    getClassName(fileIconStyle)
                  )}
                >
                  insert_drive_file
                </Icon>
                <Typography
                  variant="caption"
                  sx={{ ml: '10px' }}
                  className={cx(
                    classNames?.fileName,
                    getClassName(fileNameStyle)
                  )}
                >
                  {file.name}
                </Typography>
                <Button
                  onClick={() => onRemove(file, index)}
                  sx={{ marginLeft: 'auto' }}
                  variant="text"
                  color="inherit"
                  className={cx(
                    classNames?.fileDeleteButton,
                    getClassName(fileDeleteButtonStyle)
                  )}
                >
                  <Icon
                    className={cx(
                      classNames?.fileDeleteIcon,
                      getClassName(fileDeleteIconStyle)
                    )}
                  >
                    clear
                  </Icon>
                </Button>
              </FileItem>
            ))}
        </FilesWrapper>
      </>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  FileUpload.displayName = 'FileUpload'
}
